import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Background, Controls, MiniMap, MarkerType, useEdgesState, useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { toast } from "sonner";
import { Loader2, Filter, RefreshCw, X, User, Building2, GraduationCap, Trophy, Users, Compass } from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";

/**
 * Ecosystem Map — canvas interactif react-flow des cibles / mentors / partenaires.
 * Backend : GET /api/intelligence/ecosystem-map
 */

const GROUP_STYLE = {
  advisor:      { bg: "#0B2545", fg: "#FFFFFF", border: "#0B2545" },
  prospect:     { bg: "#FBF6EA", fg: "#7A5B14", border: "#C5A880" },
  partner:      { bg: "#E0F2FE", fg: "#075985", border: "#38BDF8" },
  professional: { bg: "#EDE9FE", fg: "#5B21B6", border: "#A78BFA" },
  org:          { bg: "#DCFCE7", fg: "#166534", border: "#4ADE80" },
  event:        { bg: "#FCE7F3", fg: "#9D174D", border: "#F472B6" },
  other:        { bg: "#F3F4F6", fg: "#4B5563", border: "#9CA3AF" },
};

const GROUP_LABELS = {
  advisor: "Moi",
  prospect: "Prospects",
  partner: "Partenaires",
  professional: "Professionnels",
  org: "Organisations",
  event: "Événements",
  other: "Autres",
};

const EDGE_STYLE = {
  owns:        { stroke: "#94A3B8", strokeWidth: 1, opacity: 0.5, dashed: true },
  same_org:    { stroke: "#7A5B14", strokeWidth: 2, opacity: 0.8, dashed: false },
  same_market: { stroke: "#0B2545", strokeWidth: 1, opacity: 0.4, dashed: true },
  explicit:    { stroke: "#059669", strokeWidth: 2, opacity: 0.9, dashed: false },
};

function nodePosition(index, total) {
  // Radial layout around advisor node at (0,0).
  if (index === 0) return { x: 0, y: 0 };
  const angle = (index / Math.max(1, total - 1)) * 2 * Math.PI;
  const ring = 1 + Math.floor(index / 12);
  const r = 220 * ring;
  return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
}


export default function EcosystemMap() {
  const [loading, setLoading] = useState(true);
  const [graph, setGraph] = useState(null);
  const [selected, setSelected] = useState(null);
  const [typesFilter, setTypesFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typesFilter.length) params.set("types", typesFilter.join(","));
      const { data } = await api.get(`/intelligence/ecosystem-map?${params.toString()}`);
      setGraph(data);
      const rfNodes = data.nodes.map((n, i) => {
        const style = GROUP_STYLE[n.group] || GROUP_STYLE.other;
        return {
          id: n.id,
          data: { label: n.label, node: n },
          position: nodePosition(i, data.nodes.length),
          style: {
            background: style.bg,
            color: style.fg,
            border: `2px solid ${style.border}`,
            borderRadius: 8,
            fontSize: n.group === "advisor" ? 13 : 11,
            fontFamily: "ui-sans-serif, system-ui",
            padding: n.group === "advisor" ? 12 : 8,
            minWidth: 120,
            maxWidth: 180,
            textAlign: "center",
            fontWeight: n.group === "advisor" ? 700 : 500,
          },
        };
      });
      const rfEdges = data.edges.map((e) => {
        const s = EDGE_STYLE[e.kind] || EDGE_STYLE.owns;
        return {
          id: e.id, source: e.source, target: e.target,
          style: { stroke: s.stroke, strokeWidth: s.strokeWidth, opacity: s.opacity,
                   strokeDasharray: s.dashed ? "5 5" : undefined },
          markerEnd: e.kind === "explicit" ? { type: MarkerType.ArrowClosed, color: s.stroke } : undefined,
        };
      });
      setNodes(rfNodes);
      setEdges(rfEdges);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de charger l'écosystème.");
    } finally { setLoading(false); }
  }, [typesFilter, setNodes, setEdges]);

  useEffect(() => { load(); }, [load]);

  const availableTypes = useMemo(() => {
    if (!graph) return [];
    return Object.entries(graph.stats?.by_group || {}).map(([k, v]) => ({ key: k, count: v }));
  }, [graph]);

  const onNodeClick = (_e, node) => {
    setSelected(node.data?.node || null);
  };

  return (
    <IntelligenceHub activeTab="map">
      <div className="relative -mx-4 md:-mx-6" data-testid="intel-map-page">
        {/* Toolbar */}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            data-testid="intel-map-filter-toggle"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white/95 backdrop-blur px-3 py-2 text-xs font-medium hover:border-[#0B2545]"
          >
            <Filter className="h-3.5 w-3.5" /> Filtres {typesFilter.length > 0 && `(${typesFilter.length})`}
          </button>
          <button
            onClick={load}
            data-testid="intel-map-refresh"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white/95 backdrop-blur px-3 py-2 text-xs font-medium hover:border-[#0B2545]"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Actualiser
          </button>
        </div>

        {/* Legend */}
        <div className="absolute bottom-3 left-3 z-10 rounded-md border border-[#E4E1D8] bg-white/95 backdrop-blur px-3 py-2 text-[10px]"
             data-testid="intel-map-legend">
          <div className="font-mono uppercase tracking-widest text-[#7A5B14] mb-1.5">Légende</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {Object.entries(GROUP_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: GROUP_STYLE[k]?.bg, border: `1px solid ${GROUP_STYLE[k]?.border}` }} />
                <span className="text-[#5B5B62]">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="absolute top-14 left-3 z-10 rounded-lg border border-[#E4E1D8] bg-white shadow-lg p-4 w-72"
               data-testid="intel-map-filters">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-sm text-[#0B2545]">Filtrer par type</div>
              <button onClick={() => setShowFilters(false)} className="text-[#5B5B62] hover:text-[#0B2545]">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {["prospect","partner","accountant","lawyer","notary","real_estate_broker","recruiter","organization","employer","event","influencer","other"].map((t) => (
                <label key={t} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#F8F7F3] rounded px-1.5 py-1">
                  <input
                    type="checkbox"
                    checked={typesFilter.includes(t)}
                    onChange={(e) => setTypesFilter((prev) =>
                      e.target.checked ? [...prev, t] : prev.filter((x) => x !== t))}
                    data-testid={`intel-map-filter-${t}`}
                  />
                  <span className="text-[#1F2937]">{t}</span>
                </label>
              ))}
            </div>
            {typesFilter.length > 0 && (
              <button
                onClick={() => setTypesFilter([])}
                className="mt-3 w-full text-xs text-[#0B2545] hover:underline"
              >
                Réinitialiser
              </button>
            )}
          </div>
        )}

        {/* Detail drawer */}
        {selected && (
          <div className="absolute top-3 right-3 z-10 w-80 rounded-lg border border-[#E4E1D8] bg-white shadow-lg p-4"
               data-testid="intel-map-drawer">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                  {selected.type || selected.group}
                </div>
                <div className="font-display text-lg text-[#0B2545] leading-tight">{selected.label}</div>
                {selected.sub_label && (
                  <div className="text-xs text-[#5B5B62] mt-0.5">{selected.sub_label}</div>
                )}
              </div>
              <button onClick={() => setSelected(null)} className="text-[#5B5B62] hover:text-[#0B2545]">
                <X className="h-4 w-4" />
              </button>
            </div>

            {selected.ai_score != null && (
              <div className="mt-3 text-xs text-[#5B5B62]">
                Score IA · <span className="font-mono font-bold tabular-nums text-[#0B2545]">{selected.ai_score}</span>/100
              </div>
            )}
            {selected.stage && (
              <div className="text-xs text-[#5B5B62]">
                Étape pipeline · <span className="text-[#1F2937]">{selected.stage}</span>
              </div>
            )}
            {selected.meta?.email && <div className="text-xs text-[#5B5B62] truncate mt-1">✉ {selected.meta.email}</div>}
            {selected.meta?.phone && <div className="text-xs text-[#5B5B62]">☎ {selected.meta.phone}</div>}
            {selected.meta?.city && <div className="text-xs text-[#5B5B62]">📍 {selected.meta.city}</div>}
            {selected.meta?.sector && <div className="text-xs text-[#5B5B62]">🏷 {selected.meta.sector}</div>}
            {selected.id && selected.group !== "advisor" && (
              <a
                href={`/app/commercial/list?target=${selected.id}`}
                data-testid="intel-map-drawer-goto"
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0B2545] hover:underline"
              >
                Ouvrir la fiche →
              </a>
            )}
          </div>
        )}

        {/* Canvas */}
        <div className="h-[700px] w-full bg-[#F8F7F3] rounded-lg border border-[#E4E1D8] overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center text-[#5B5B62]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Assemblage de l&apos;écosystème…
            </div>
          ) : nodes.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <Compass className="h-10 w-10 text-[#7A5B14] mb-3" strokeWidth={1.5} />
              <div className="font-display text-lg text-[#1F2937]">Aucune cible visible</div>
              <p className="text-sm text-[#5B5B62] max-w-md mt-2">
                Créez ou importez vos premières cibles pour visualiser votre écosystème.
              </p>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes} edges={edges}
              onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              fitView proOptions={{ hideAttribution: true }}
              defaultEdgeOptions={{ animated: false }}
              minZoom={0.2} maxZoom={2}
            >
              <Background color="#E4E1D8" gap={24} />
              <Controls showInteractive={false} />
              <MiniMap
                nodeColor={(n) => n.style?.background || "#E4E1D8"}
                maskColor="rgba(11, 37, 69, 0.06)"
                style={{ backgroundColor: "#F8F7F3", border: "1px solid #E4E1D8" }}
              />
            </ReactFlow>
          )}
        </div>

        {/* Stats footer */}
        {graph?.stats && (
          <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-[#5B5B62] flex items-center gap-4 justify-center"
               data-testid="intel-map-stats">
            <span>{graph.stats.total_nodes} nœuds</span>
            <span>·</span>
            <span>{graph.stats.total_edges} relations</span>
            {graph.stats.total_nodes >= graph.stats.capped_at_nodes && (
              <span className="text-[#7A5B14]">· plafond {graph.stats.capped_at_nodes} atteint</span>
            )}
          </div>
        )}
      </div>
    </IntelligenceHub>
  );
}
